import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Row, Col, Typography, Form, message, Divider, Spin, Table } from 'antd';
import { SearchOutlined, EnvironmentOutlined, GlobalOutlined } from '@ant-design/icons';
import { admin as adminService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

const CDComponent = ({ embedded = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchType, setSearchType] = useState(null); // 'cep' ou 'list'
  const [formSigla] = Form.useForm();
  const [formCep] = Form.useForm();
  const [formRota] = Form.useForm();
  const [formUf] = Form.useForm();

  const empresa = user?.empresa || localStorage.getItem('empresa');

  const columns = [
    {
      title: 'CD',
      dataIndex: 'cd',
      key: 'cd',
      align: 'center',
    },
    {
      title: 'Rota',
      dataIndex: 'rota',
      key: 'rota',
      align: 'center',
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      align: 'center',
    },
  ];

  const handleSearchBySigla = async (values) => {
    try {
      setLoading(true);
      setSearchType('list');
      const response = await adminService.getRotasGrouped({ cd: values.sigla });
      if (response && response.length > 0) {
        setSearchResults(response.map((item, index) => ({ ...item, id: index })));
      } else {
        message.warning('Nenhum CD encontrado para esta sigla');
        setSearchResults([]);
      }
    } catch (error) {
      message.error('Erro ao buscar por sigla');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByCep = async (values) => {
    try {
      setLoading(true);
      setSearchType('cep');
      const data = await adminService.getRotasByCep(values.cep);
      if (data) {
        setSearchResults([data]);
      } else {
        message.warning('Nenhum CD encontrado para este CEP');
        setSearchResults([]);
      }
    } catch (error) {
      message.error('Erro ao buscar por CEP');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByRota = async (values) => {
    try {
      setLoading(true);
      setSearchType('list');
      const response = await adminService.getRotasGrouped({ rota: values.rota });
      if (response && response.length > 0) {
        setSearchResults(response.map((item, index) => ({ ...item, id: index })));
      } else {
        message.warning('Nenhuma rota encontrada');
        setSearchResults([]);
      }
    } catch (error) {
      message.error('Erro ao buscar por rota');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByUf = async (values) => {
    try {
      setLoading(true);
      setSearchType('list');
      const response = await adminService.getRotasGrouped({ uf: values.uf });
      if (response && response.length > 0) {
        setSearchResults(response.map((item, index) => ({ ...item, id: index })));
      } else {
        message.warning('Nenhuma UF encontrada');
        setSearchResults([]);
      }
    } catch (error) {
      message.error('Erro ao buscar por UF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: embedded ? '0' : '24px' }}>
      {!embedded && <Title level={2}>Consulta de CDs e Rotas</Title>}
      
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} md={6}>
          <Card title="Busca por Sigla">
            <Form form={formSigla} layout="inline" onFinish={handleSearchBySigla}>
              <Form.Item name="sigla" rules={[{ required: true, message: 'Informe a sigla' }]}>
                <Input placeholder="Sigla" style={{ width: '80px', textTransform: 'uppercase' }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SearchOutlined />} htmlType="submit">Buscar</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        
        <Col xs={24} md={6}>
          <Card title="Busca por CEP">
            <Form form={formCep} layout="inline" onFinish={handleSearchByCep}>
              <Form.Item name="cep" rules={[{ required: true, message: 'Informe o CEP' }]}>
                <Input placeholder="CEP" style={{ width: '100px' }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<EnvironmentOutlined />} htmlType="submit">Buscar</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        
        <Col xs={24} md={6}>
          <Card title="Busca por Rota">
            <Form form={formRota} layout="inline" onFinish={handleSearchByRota}>
              <Form.Item name="rota" rules={[{ required: true, message: 'Rota' }]}>
                <Input placeholder="Rota" style={{ width: '80px', textTransform: 'uppercase' }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SearchOutlined />} htmlType="submit">Buscar</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card title="Busca por UF">
            <Form form={formUf} layout="inline" onFinish={handleSearchByUf}>
              <Form.Item name="uf" rules={[{ required: true, message: 'UF' }]}>
                <Input placeholder="UF" style={{ width: '60px', textTransform: 'uppercase' }} maxLength={2} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<GlobalOutlined />} htmlType="submit">Buscar</Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {searchResults.length > 0 && searchType === 'cep' && (
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
          <Card 
            style={{ width: 350, textAlign: 'center' }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ padding: '20px' }}>
              <Text strong style={{ fontSize: '16px' }}>Sigla</Text>
              <Title level={1} style={{ margin: '10px 0', color: '#722ed1', fontSize: '48px' }}>{searchResults[0].rota}</Title>
            </div>
            <div style={{ backgroundColor: '#bae7ff', padding: '20px' }}>
              <Text strong style={{ fontSize: '16px' }}>C.D</Text>
              <Title level={1} style={{ margin: '10px 0', fontSize: '48px' }}>{searchResults[0].cd}</Title>
              {searchResults[0].usuario && (
                <div>
                  <Text strong>Usuário: </Text>
                  <Text style={{ fontSize: '20px' }}>{searchResults[0].usuario}</Text>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {searchResults.length > 0 && searchType === 'list' && (
        <div style={{ marginBottom: '24px' }}>
          <Table 
            dataSource={searchResults} 
            columns={columns} 
            rowKey="id"
            pagination={{ pageSize: 10 }}
            bordered
          />
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" tip="Buscando..." />
        </div>
      )}
    </div>
  );
};

export default CDComponent;
